import React, { FC, memo, useContext } from 'react'
import { ThemeContext } from 'styled-components'
import { useTranslation } from '../../../hooks/locale'
import { useCardData } from '../../../hooks/use-card-data'
import { ListingSectionObject } from '../../../transports/common/listing-section-transport'
import {
    BottomRow,
    ButtonWrapper,
    CTAButton,
    PriceWrapper,
    GridCardContainer,
    ImageContainer,
    CardContent,
    CardTopSection,
    CardBottomContainer,
    LocationText,
    TopTraumTagPill,
    RatingText,
    RatingCount,
    PropertyTitle,
    PropertyDescription,
    PriceContainer,
    FavoriteButton,
    PartnerLogoContainer,
    PartnerLogo,
    FullStarIcon,
    LocationRatingContainer,
    LocationContainer,
    PersonDetails,
    RatingBadge,
    OfferBadge,
    OfferIcon,
    OfferText,
    ObjectTopRecommendationPill,
    NextImageContainer,
    FeatureIconsContainer,
    CheckBox,
    InputCheckbox,
    CheckBoxLabel,
    InformationWrapper,
    FreeCancellationContainer,
    FreeCancellationIcon,
    FreeCancellationText,
    TraumTagIconStyled,
    TraumTagLabel,
    OfferWrapper,
    PopularChoiceTagContainer,
    PopularChoiceTagText,
    BottomTraumTagContainer,
    BottomTraumTagImage,
    BottomTraumTagText,
    SeoHref,
    PropertyTitleLink,
    BookingHighlights,
    QuickResponderTag,
    StyledFastResponderIcon,
} from './styles'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'
import { Features } from '../../../models/common/features-model'
import { Offer } from '../../../models/common/offers-model'
import { SuitablesModel } from '../../../models/common/suitables-model'
import get from 'lodash/get'

import NextImage from '../next-image'
import InlineFeatures from '../features/features'
import Star from '../../../assets/icons/star.svg'
import WishlistSection from '../wishlist-section'
import { preventDefaultHandler } from '../../utils'
import { stopPropagation } from '../../../utils/dom-utils'
import ImageCarousel from '../../common/image-carousel'
import { getPartnerLogoUrl } from '../../../utils/miscellaneous-utils'

interface GridCardProps {
    listingObject: ListingSectionObject
    requestUrl?: string
    sitePathname?: string
    displayPrice?: boolean
    displayPriceInfo?: boolean
    features?: Features
    showCtaButton?: boolean
    isObjectOverview?: boolean
    pageName: string
    sectionName: string
    showMaxPersonCount?: boolean
    showCheckbox?: boolean
    isSelected?: boolean
    onToggleSelect?: (id: number) => void
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
    useCarousel?: boolean
}

const GridCard: FC<GridCardProps> = ({
    listingObject,
    requestUrl,
    sitePathname,
    displayPrice = true,
    displayPriceInfo = true,
    features,
    showCtaButton = false,
    isObjectOverview = false,
    pageName,
    sectionName,
    showMaxPersonCount = false,
    showCheckbox = false,
    isSelected = false,
    onToggleSelect,
    breadcrumbModel,
    offerModel,
    suitablesModel,
    useCarousel = false,
}) => {
    const theme = useContext(ThemeContext)
    const { t } = useTranslation()

    const cardData = useCardData({
        listingObject,
        features,
        requestUrl,
        sitePathname,
        pageName,
        sectionName,
        displayPrice,
        displayPriceInfo,
        showCheckbox,
        showMaxPersonCount,
        isSelected,
        onToggleSelect,
        isObjectOverview,
        breadcrumbModel,
        offerModel,
        suitablesModel,
    })

    const {
        listingId,
        propertyTitle,
        propertyDescriptionText,
        regionName,
        regionLink,
        propertyImage,
        priceValue,
        priceInfoValue,
        formattedOffer,
        isFreeCancellation,
        shouldShowRating,
        formattedRatingAverage,
        formattedRatingCount,
        hasFeatures,
        traumTagsData,
        currentBadgeTraumTag,
        isTopObject,
        intersectionTargetRef,
        handleCardClick,
        handleCheckboxChange,
        handleCtaClick,
        href,
        seoHref,
        noCrawl,
        showSuperfastResponderTag,
        isDirectBooking,
        cardImages,
        partnerName,
    } = cardData

    if (!listingObject) {
        return null
    }

    return (
        <GridCardContainer ref={intersectionTargetRef} onClick={handleCardClick} $isObjectOverview={isObjectOverview}>
            <ImageContainer>
                {showCheckbox && (
                    <InformationWrapper onClick={stopPropagation}>
                        <CheckBox>
                            <InputCheckbox
                                type="checkbox"
                                checked={isSelected}
                                onChange={handleCheckboxChange}
                                id={`checkbox-${listingId}`}
                            />
                            <CheckBoxLabel htmlFor={`checkbox-${listingId}`} />
                        </CheckBox>
                    </InformationWrapper>
                )}
                {useCarousel ? (
                    <ImageCarousel
                        images={cardImages}
                        aspectRatio="76.22%"
                        imageCarouselBorderRadius="1rem 1rem 0 0"
                        imageCarouselMargin="0"
                    />
                ) : (
                    !!propertyImage && (
                        <NextImage
                            containerComponent={NextImageContainer}
                            src={get(propertyImage, 'url', '')}
                            alt={get(propertyImage, 'alt', 'Property image')}
                            layout="responsive"
                            // Width and Height are only used for aspect ratio here.
                            width={143}
                            height={109}
                        />
                    )
                )}
                {traumTagsData.badgeTraumTags.length > 0 && currentBadgeTraumTag && currentBadgeTraumTag.Icon && (
                    <TopTraumTagPill key={currentBadgeTraumTag.id} $showCheckbox={showCheckbox}>
                        <TraumTagIconStyled as={currentBadgeTraumTag.Icon} />
                        <TraumTagLabel>{t(currentBadgeTraumTag.label)}</TraumTagLabel>
                    </TopTraumTagPill>
                )}
                {isTopObject && <ObjectTopRecommendationPill>{t('topListing')}</ObjectTopRecommendationPill>}
                {partnerName ? (
                    <PartnerLogoContainer $partnerName={partnerName}>
                        <PartnerLogo
                            src={getPartnerLogoUrl(partnerName)}
                            alt={`${partnerName} logo`}
                            $partnerName={partnerName}
                        />
                    </PartnerLogoContainer>
                ) : (
                    <FavoriteButton onClick={stopPropagation}>
                        <WishlistSection
                            containerBorderRadius="1.456rem"
                            containerHeight="1.5rem"
                            containerWidth="1.5rem"
                            containerMargin="0"
                            containerPadding="0"
                            width="1.5rem"
                            height="1.5rem"
                            listingId={listingId?.toString() || ''}
                            showBoxShadow={false}
                            strokeColor="white"
                            containerBackgroundColor="transparent"
                        />
                    </FavoriteButton>
                )}

                {shouldShowRating && (
                    <RatingBadge>
                        <FullStarIcon as={Star} />
                        <RatingText>
                            {formattedRatingAverage} <RatingCount>{formattedRatingCount}</RatingCount>
                        </RatingText>
                    </RatingBadge>
                )}
            </ImageContainer>

            <CardContent $hasBottomTags={traumTagsData.hasBottomTags}>
                <CardTopSection>
                    <LocationRatingContainer>
                        <LocationContainer>
                            <SeoHref title={regionName} href={regionLink} onClick={stopPropagation}>
                                <LocationText>{regionName}</LocationText>
                            </SeoHref>
                        </LocationContainer>
                        {traumTagsData.popularChoiceTag && (
                            <PopularChoiceTagContainer>
                                {traumTagsData.popularChoiceTag.Icon && (
                                    <TraumTagIconStyled as={traumTagsData.popularChoiceTag.Icon} />
                                )}
                                <PopularChoiceTagText>{t(traumTagsData.popularChoiceTag.label)}</PopularChoiceTagText>
                            </PopularChoiceTagContainer>
                        )}
                    </LocationRatingContainer>
                    <PropertyTitle>
                        <PropertyTitleLink
                            autoRedirect={false}
                            title={propertyTitle}
                            href={seoHref ?? href}
                            onClick={preventDefaultHandler}
                            noCrawl={noCrawl ?? false}
                        >
                            {propertyTitle}
                        </PropertyTitleLink>
                    </PropertyTitle>

                    {propertyDescriptionText && <PropertyDescription>{propertyDescriptionText}</PropertyDescription>}

                    {hasFeatures && (
                        <FeatureIconsContainer>
                            <InlineFeatures
                                features={features || null}
                                color={theme.colors.textPrimary}
                                size={1.2}
                                gap={0.5}
                                visibleFeatures={5}
                            />
                        </FeatureIconsContainer>
                    )}
                </CardTopSection>

                <BookingHighlights>
                    {showSuperfastResponderTag && (
                        <QuickResponderTag>
                            <StyledFastResponderIcon /> {t('quickResponderText')}
                        </QuickResponderTag>
                    )}
                    {isDirectBooking && (
                        <QuickResponderTag>
                            <StyledFastResponderIcon /> {t('directBookingTagLabel')}
                        </QuickResponderTag>
                    )}
                </BookingHighlights>

                <CardBottomContainer>
                    <BottomRow>
                        {formattedOffer && (
                            <OfferBadge>
                                <OfferIcon />
                                <OfferText>
                                    {get(formattedOffer, 'discount', '')} ● {get(formattedOffer, 'label', '')}
                                </OfferText>
                            </OfferBadge>
                        )}

                        <PriceWrapper>
                            {isFreeCancellation && (
                                <FreeCancellationContainer>
                                    <FreeCancellationIcon />
                                    <FreeCancellationText>{t('freeCancellation')}</FreeCancellationText>
                                </FreeCancellationContainer>
                            )}
                            {displayPrice && priceValue && <PriceContainer>{priceValue}</PriceContainer>}
                            {displayPriceInfo && priceInfoValue && <PersonDetails>{priceInfoValue}</PersonDetails>}
                        </PriceWrapper>

                        <OfferWrapper>
                            {traumTagsData.bottomTraumTags.map(tag => (
                                <BottomTraumTagContainer key={tag.id}>
                                    {tag.imageUrl && <BottomTraumTagImage src={tag.imageUrl} alt={t(tag.label)} />}
                                    <BottomTraumTagText>{t(tag.label)}</BottomTraumTagText>
                                </BottomTraumTagContainer>
                            ))}
                        </OfferWrapper>
                    </BottomRow>

                    {showCtaButton && (
                        <ButtonWrapper>
                            <CTAButton onClick={handleCtaClick}>{t('enquire')}</CTAButton>
                        </ButtonWrapper>
                    )}
                </CardBottomContainer>
            </CardContent>
        </GridCardContainer>
    )
}

export default memo(GridCard)
